import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storesApi, revenueApi, accountsApi } from '../services/api';
import { formatCurrency, dollarsToCents } from 'shared';

type Mode = 'monthly' | 'annual';

const currentYear = new Date().getFullYear();
const priorYears = [currentYear - 1, currentYear - 2, currentYear - 3].map(String);

export function RevenueEntry() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('monthly');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedYear, setSelectedYear] = useState(String(currentYear - 1));
  const [revenueInputs, setRevenueInputs] = useState<Record<string, string>>({});
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const priorYear = currentYear - 1;

  const { periodStart, periodEnd } = useMemo(() => {
    if (mode === 'monthly') {
      const [y, m] = selectedMonth.split('-').map(Number);
      return {
        periodStart: `${y}-${String(m).padStart(2, '0')}-01`,
        periodEnd: new Date(y, m, 0).toISOString().split('T')[0],
      };
    }
    return { periodStart: `${selectedYear}-01-01`, periodEnd: `${selectedYear}-12-31` };
  }, [mode, selectedMonth, selectedYear]);

  const { data: accounts = [], isLoading: loadingAccounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  const { data: stores = [], isLoading: loadingStores } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
  });

  const { data: accountPriorYear = {} } = useQuery({
    queryKey: ['accounts', 'annual', priorYear],
    queryFn: () => accountsApi.getAnnualSummary(priorYear),
  });

  const { data: storeRevenueSnapshots = [] } = useQuery({
    queryKey: ['revenue', 'annual', priorYear],
    queryFn: () => revenueApi.list({ from: `${priorYear}-01-01`, to: `${priorYear}-12-31` }),
  });

  const storePriorYear = storeRevenueSnapshots.reduce<Record<string, number>>((acc, snap) => {
    acc[snap.store_id] = (acc[snap.store_id] || 0) + snap.revenue_cents;
    return acc;
  }, {});

  // Existing revenue for the currently selected period (monthly mode only)
  const { data: existingPeriodStoreRevenue = [] } = useQuery({
    queryKey: ['revenue', 'period', periodStart, periodEnd],
    queryFn: () => revenueApi.list({ from: periodStart, to: periodEnd }),
    enabled: mode === 'monthly',
  });

  const { data: existingPeriodAccountRevenue = {} } = useQuery({
    queryKey: ['accounts', 'revenue', 'period', periodStart, periodEnd],
    queryFn: () => accountsApi.getRevenueByPeriod(periodStart, periodEnd),
    enabled: mode === 'monthly',
  });

  const existingStoreRevenue = existingPeriodStoreRevenue.reduce<Record<string, number>>((acc, snap) => {
    acc[snap.store_id] = (acc[snap.store_id] || 0) + snap.revenue_cents;
    return acc;
  }, {});

  const saveAccountRevenueMutation = useMutation({
    mutationFn: async ({ accountId, revenue, periodStart, periodEnd }: {
      accountId: string; revenue: number; periodStart: string; periodEnd: string;
    }) => {
      return accountsApi.addRevenue(accountId, {
        period_start: periodStart,
        period_end: periodEnd,
        revenue_cents: dollarsToCents(revenue),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', 'revenue', 'period'] });
    },
  });

  const saveStoreRevenueMutation = useMutation({
    mutationFn: async ({ storeId, revenue, periodStart, periodEnd }: {
      storeId: string; revenue: number; periodStart: string; periodEnd: string;
    }) => {
      return revenueApi.create({
        store_id: storeId,
        period_start: periodStart,
        period_end: periodEnd,
        revenue_cents: dollarsToCents(revenue),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['revenue'] });
    },
  });

  const getPeriod = () => ({ periodStart, periodEnd });

  const handleSaveAccount = (accountId: string, inputKey: string) => {
    const raw = revenueInputs[inputKey];
    const value = raw !== undefined ? raw : (existingPeriodAccountRevenue[accountId] ? (existingPeriodAccountRevenue[accountId] / 100).toFixed(2) : '');
    if (!value) return;
    const revenue = parseFloat(value);
    if (isNaN(revenue) || revenue < 0) return;
    const { periodStart, periodEnd } = getPeriod();
    saveAccountRevenueMutation.mutate({ accountId, revenue, periodStart, periodEnd });
    setRevenueInputs((prev) => { const next = { ...prev }; delete next[inputKey]; return next; });
    setSavedKey(inputKey);
    setTimeout(() => setSavedKey(null), 2000);
  };

  const handleSaveStore = (storeId: string, inputKey: string) => {
    const raw = revenueInputs[inputKey];
    const value = raw !== undefined ? raw : (existingStoreRevenue[storeId] ? (existingStoreRevenue[storeId] / 100).toFixed(2) : '');
    if (!value) return;
    const revenue = parseFloat(value);
    if (isNaN(revenue) || revenue < 0) return;
    const { periodStart, periodEnd } = getPeriod();
    saveStoreRevenueMutation.mutate({ storeId, revenue, periodStart, periodEnd });
    setRevenueInputs((prev) => { const next = { ...prev }; delete next[inputKey]; return next; });
    setSavedKey(inputKey);
    setTimeout(() => setSavedKey(null), 2000);
  };

  const standaloneStores = stores
    .filter((s) => !s.account_id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const sortedAccounts = [...accounts].sort((a, b) => a.name.localeCompare(b.name));

  const periodLabel = mode === 'monthly' ? selectedMonth : `${selectedYear} (Full Year)`;

  if (loadingAccounts || loadingStores) {
    return <div className="text-center py-8 text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Revenue Entry</h2>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Mode toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            <button
              onClick={() => { setMode('monthly'); setRevenueInputs({}); }}
              className={`px-4 py-2 font-medium transition-colors ${
                mode === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => { setMode('annual'); setRevenueInputs({}); }}
              className={`px-4 py-2 font-medium transition-colors border-l border-gray-300 ${
                mode === 'annual' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Annual (Prior Year)
            </button>
          </div>

          {/* Period picker */}
          {mode === 'monthly' ? (
            <input
              type="month"
              value={selectedMonth}
              max={`${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
              onChange={(e) => { setSelectedMonth(e.target.value); setRevenueInputs({}); }}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          ) : (
            <select
              value={selectedYear}
              onChange={(e) => { setSelectedYear(e.target.value); setRevenueInputs({}); }}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {priorYears.map((y) => (
                <option key={y} value={y}>{y} Full Year</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {mode === 'annual' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          Enter the full-year revenue total for <strong>{selectedYear}</strong>. This will be saved as an annual snapshot and used for historical reference. To backfill monthly data for the current year, switch to <strong>Monthly</strong> mode and pick any past month.
        </div>
      )}

      {/* Parent Accounts */}
      {sortedAccounts.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-2">Parent Accounts</h3>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{currentYear} YTD / {priorYear}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stores</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue — {periodLabel}
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedAccounts.map((account) => {
                  const key = `account_${account.id}`;
                  return (
                    <tr key={account.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{account.name}</div>
                        {account.contact_name && (
                          <div className="text-xs text-gray-500">{account.contact_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          account.priority?.account_size === 'large'
                            ? 'bg-purple-100 text-purple-800'
                            : account.priority?.account_size === 'medium'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {account.priority?.account_size || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(account.priority?.ytd_revenue_cents || 0)}</div>
                        {accountPriorYear[account.id] > 0 && (
                          <div className="text-xs text-gray-400 mt-0.5">{priorYear}: {formatCurrency(accountPriorYear[account.id])}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.stores.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const existing = mode === 'monthly' ? existingPeriodAccountRevenue[account.id] : undefined;
                          const displayValue = revenueInputs[key] !== undefined
                            ? revenueInputs[key]
                            : existing ? (existing / 100).toFixed(2) : '';
                          return (
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={displayValue}
                                  onChange={(e) => setRevenueInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                                  placeholder="0.00"
                                  className={`w-32 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${existing && revenueInputs[key] === undefined ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                                />
                              </div>
                              {existing && revenueInputs[key] === undefined && (
                                <div className="text-xs text-green-600 mt-0.5">Saved for this period</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {savedKey === key ? (
                          <span className="text-green-600">Saved!</span>
                        ) : (
                          <button
                            onClick={() => handleSaveAccount(account.id, key)}
                            disabled={saveAccountRevenueMutation.isPending}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Standalone Stores */}
      {standaloneStores.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-2">Standalone Stores</h3>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{currentYear} YTD / {priorYear}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visit Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue — {periodLabel}
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {standaloneStores.map((store) => {
                  const key = `store_${store.id}`;
                  return (
                    <tr key={store.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{store.name}</div>
                        {store.chain_name && (
                          <div className="text-sm text-gray-500">{store.chain_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          store.priority?.account_size === 'large'
                            ? 'bg-purple-100 text-purple-800'
                            : store.priority?.account_size === 'medium'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {store.priority?.account_size || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(store.priority?.ytd_revenue_cents || 0)}</div>
                        {storePriorYear[store.id] > 0 && (
                          <div className="text-xs text-gray-400 mt-0.5">{priorYear}: {formatCurrency(storePriorYear[store.id])}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {store.priority?.visit_duration_minutes
                          ? `${store.priority.visit_duration_minutes} min`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const existing = mode === 'monthly' ? existingStoreRevenue[store.id] : undefined;
                          const displayValue = revenueInputs[key] !== undefined
                            ? revenueInputs[key]
                            : existing ? (existing / 100).toFixed(2) : '';
                          return (
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={displayValue}
                                  onChange={(e) => setRevenueInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                                  placeholder="0.00"
                                  className={`w-32 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${existing && revenueInputs[key] === undefined ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                                />
                              </div>
                              {existing && revenueInputs[key] === undefined && (
                                <div className="text-xs text-green-600 mt-0.5">Saved for this period</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {savedKey === key ? (
                          <span className="text-green-600">Saved!</span>
                        ) : (
                          <button
                            onClick={() => handleSaveStore(store.id, key)}
                            disabled={saveStoreRevenueMutation.isPending}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sortedAccounts.length === 0 && standaloneStores.length === 0 && (
        <p className="text-gray-500 text-center py-8">No stores or accounts found.</p>
      )}
    </div>
  );
}
